import {View, Text} from 'react-native';
import React, {useState} from 'react';
import {useStyle} from 'src/providers/style';
import {useDimensions} from '@react-native-community/hooks';

import {
  calculateWeeklyServiceHours,
  serviceTypesMap,
} from 'src/common-utils/weekly-service-hours';
import {Colors, Typography} from 'src/styles';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryStack,
  VictoryTheme,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory-native';
import {ForeignObject, G} from 'react-native-svg';
import {boxShadow} from 'src/styles/mixins';
import moment from 'moment';
import {Separator} from 'src/common-components/atoms';
import InformationTooltip from 'src/modules/programs/components/information-tooltip';

const tooltipText = `Breakdown of weekly service hours by type:
Direct Treatment (97153) vs. Clinical Services (other billable codes)
Example: 12.5 hrs total → 5.0 Direct (40%) → 7.5 Clinical (60%)`;

const HTMLFlyOut = ({x, y, datum, data}: any) => {
  const styles = useStyle();
  const weekData = data[datum._group];

  return (
    <G>
      <ForeignObject
        x={x - 70}
        y={Math.max(y / 2, 10)}
        width="160"
        height="600"
        /* eslint-disable-next-line react-native/no-inline-styles */
        style={{overflow: 'visible'}}>
        <View
          style={[
            styles.borderRadius20,
            styles.backgroundColorWhite,
            styles.paddingL,
            boxShadow(Colors.RAVEN_BLACK),
          ]}>
          <View style={[styles.paddingSMBottom]}>
            <Text style={[Typography.CAPTION_MEDIUM]}>Total Hours</Text>
            <Text style={[Typography.CAPTION, styles.paddingSMBottom]}>
              {weekData?.total || 0}
            </Text>
          </View>

          {Object.keys(serviceTypesMap).map(typeKey =>
            weekData?.[typeKey] > 0 ? (
              <View key={typeKey} style={[styles.paddingSMVertical]}>
                <Text style={[Typography.CAPTION_MEDIUM]}>
                  {serviceTypesMap[typeKey].title}
                </Text>
                <Text style={[Typography.CAPTION]}>
                  {weekData[typeKey].toFixed(2)} hrs (
                  {weekData[`${typeKey}Percent`] || 0}%)
                </Text>
                <Text style={[Typography.CAPTION]}>
                  CPTs: {weekData[`${typeKey}CPTs`]?.join(', ')}
                </Text>
              </View>
            ) : null,
          )}
        </View>
      </ForeignObject>
    </G>
  );
};

const WeeklyServiceHours = ({sessions}: any) => {
  const styles = useStyle();
  const dimensions = useDimensions();
  const [hoveredIndex, setHoveredIndex] = useState<any>();

  const data = calculateWeeklyServiceHours(sessions);
  const maxTotal = data.length ? Math.max(...data.map(d => d.total)) : 0;
  const domain = {x: [0, data.length + 1], y: [0, Math.max(maxTotal, 1)]};

  return (
    <View style={[styles.flex, styles.card, styles.padding]}>
      <>
        <View style={[styles.row]}>
          <Text style={[Typography.P2_BOLD]}>Weekly Service Hours</Text>
          <Separator width={4} />
          <InformationTooltip value={tooltipText} />
        </View>
        <Text style={[Typography.P3, styles.alignSelfCenter]}>
          Total Service Hours by Type
        </Text>
        <VictoryChart
          theme={VictoryTheme.material}
          width={dimensions.screen.width * 0.95}
          domain={domain}
          domainPadding={{x: 40}}
          scale={{x: 'time'}}
          containerComponent={
            <VictoryVoronoiContainer
              onActivated={points => {
                const key = points[0]?.eventKey;
                if (key !== undefined) {
                  setHoveredIndex(key);
                }
              }}
              onDeactivated={() => setHoveredIndex(null)}
            />
          }>
          <VictoryAxis
            tickFormat={t => moment(t, 'YYYY-MM-DD').format('MM/DD/YY')}
            style={{
              axis: {stroke: Colors.GRAY_100},
              tickLabels: {
                ...Typography.CAPTION,
                angle: 0,
                fill: Colors.PRIMARY_600,
              },
              grid: {stroke: 'transparent'},
            }}
            label={'Week Of'}
            axisLabelComponent={
              <VictoryLabel
                style={{...Typography.CAPTION, color: Colors.TEXT_PRIMARY}}
                dy={30}
              />
            }
            tickLabelComponent={
              <VictoryLabel
                style={{...Typography.CAPTION, fill: Colors.TEXT_PRIMARY}}
              />
            }
          />
          <VictoryAxis dependentAxis tickFormat={x => `${x}`} />

          <VictoryStack>
            {Object.keys(serviceTypesMap).map(typeKey => (
              <VictoryBar
                key={typeKey}
                data={data.map(d => ({
                  x: d.week,
                  y: d[typeKey],
                }))}
                style={{
                  data: {fill: serviceTypesMap[typeKey].color},
                }}
                barWidth={30}
              />
            ))}
          </VictoryStack>

          <VictoryBar
            data={data.map((d, index) => ({
              x: d.week,
              y: d.total / 2,
              _group: index,
              _y0: 0,
              _y1: d.total,
            }))}
            barWidth={30}
            style={{data: {fill: 'transparent'}}}
            labels={({datum}) =>
              String(hoveredIndex) === String(datum._group) ? ' ' : null
            }
            labelComponent={
              <VictoryTooltip
                renderInPortal
                flyoutComponent={<HTMLFlyOut data={data} />}
              />
            }
          />
        </VictoryChart>
        <View style={[styles.paddingBottom]}>
          <View
            style={[
              styles.row,
              styles.alignCenter,
              styles.justifyCenter,
              styles.marginLTop,
            ]}>
            <View style={[styles.row, styles.alignCenter, styles.marginRight]}>
              <View
                style={[
                  styles.dotL,
                  styles.backgroundColorSecondary800,
                  styles.marginLRight,
                ]}
              />
              <Text style={[Typography.P3]}>Direct Treatment</Text>
            </View>
            <View style={[styles.row, styles.alignCenter, styles.marginRight]}>
              <View
                style={[
                  styles.dotL,
                  styles.backgroundColorTeal,
                  styles.marginLRight,
                ]}
              />
              <Text style={[Typography.P3]}>Clinical Services</Text>
            </View>
          </View>
        </View>
      </>
    </View>
  );
};

export default WeeklyServiceHours;
